import { useEffect } from 'react';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectUser } from '@/slice/user';
import { getEzeeAssistToken } from '@/api/ezeeAssist';

declare global {
  interface Window {
    ezee: {
      setupChatConfig: (config: {
        // // provide one of ...

        // // OPTION 1: public token or ...
        // publicToken: undefined,

        // // OPTION 2: private token AND userEmail or ...
        // privateToken: undefined,
        // userEmail: undefined,

        // // OPTION 3: user token
        userToken?: string;
        enableAttachments?: boolean;
      }) => void;
      initChat: () => void;
    };
  }
}

/** Maximum time (ms) to wait for window.ezee to become available. */
const MAX_WAIT_MS = 10_000;
/** Polling interval (ms) while waiting for the script to load. */
const POLL_INTERVAL_MS = 200;

const waitForEzee = (): Promise<boolean> =>
  new Promise((resolve) => {
    if (window.ezee) return resolve(true);

    let elapsed = 0;
    const id = setInterval(() => {
      elapsed += POLL_INTERVAL_MS;
      if (window.ezee) {
        clearInterval(id);
        resolve(true);
      } else if (elapsed >= MAX_WAIT_MS) {
        clearInterval(id);
        resolve(false);
      }
    }, POLL_INTERVAL_MS);
  });

/**
 * EzeeAssist component – initializes the Ezee Assist chat widget.
 * Only renders for logged-in consultants.
 * Fetches a short-lived user token from the backend to establish the websocket connection.
 */
const EzeeAssist = () => {
  const user = useAppSelector(selectUser);
  const isConsultant = user?.role === 'consultant';

  useEffect(() => {
    if (!isConsultant) return;

    let cancelled = false;

    const initEzeeAssist = async () => {
      try {
        const { enabled, token } = await getEzeeAssistToken();
        if (cancelled || !enabled || !token) return;

        const ready = await waitForEzee();
        if (cancelled || !ready) return;

        window.ezee.setupChatConfig({ userToken: token, enableAttachments: false });
        window.ezee.initChat();
      } catch (error) {
        console.error('[EzeeAssist] Failed to initialize:', error);
      }
    };

    initEzeeAssist();

    return () => {
      cancelled = true;
    };
  }, [isConsultant]);

  return null;
};

export default EzeeAssist;
